import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Sparkles,
  Loader2,
  Check,
  X,
  Trash2,
  Image,
  Copy,
  CheckSquare,
  Square,
  TrendingUp,
  Zap,
  Filter,
  Package,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { api } from '../api/client';

type TrendPrompt = {
  id: string;
  original_prompt: string;
  niche: string;
  generated_prompt: string;
  status: string;
  image_url: string | null;
  listing_id: number | null;
  total_cost_usd: number;
  created_at: string;
};

export default function Trends() {
  const queryClient = useQueryClient();

  // Form state
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [selectedNiche, setSelectedNiche] = useState('');
  const [customNiche, setCustomNiche] = useState('');
  const [promptCount, setPromptCount] = useState(5);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [nicheFilter, setNicheFilter] = useState<string>('');

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Queries
  const { data: nichesData } = useQuery({
    queryKey: ['trends-niches'],
    queryFn: () => api.trends.niches(),
  });

  const { data: promptsData, isLoading: loadingPrompts } = useQuery({
    queryKey: ['trends-prompts', statusFilter, nicheFilter],
    queryFn: () =>
      api.trends.list({
        status: statusFilter || undefined,
        niche: nicheFilter || undefined,
        limit: 100,
      }),
  });

  // Mutations
  const generateMutation = useMutation({
    mutationFn: (data: { original_prompt: string; niche: string; count: number }) =>
      api.trends.generate(data),
    onSuccess: (data) => {
      toast.success(`Generated ${data.count} prompt variations`);
      queryClient.invalidateQueries({ queryKey: ['trends-prompts'] });
      setOriginalPrompt('');
      setSelectedNiche('');
      setCustomNiche('');
    },
    onError: (error) => {
      toast.error(`Generation failed: ${error}`);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'approved' | 'rejected' }) =>
      api.trends.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trends-prompts'] });
    },
  });

  const generateImageMutation = useMutation({
    mutationFn: (id: string) => api.trends.generateImage(id, { product_type: 'apparel' }),
    onSuccess: () => {
      toast.success('Image generated successfully');
      queryClient.invalidateQueries({ queryKey: ['trends-prompts'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      toast.error(`Image generation failed: ${error}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.trends.delete(id),
    onSuccess: () => {
      toast.success('Prompt deleted');
      queryClient.invalidateQueries({ queryKey: ['trends-prompts'] });
    },
  });

  const bulkActionMutation = useMutation({
    mutationFn: (data: { ids: string[]; action: 'approve' | 'reject' | 'delete' }) =>
      api.trends.bulkAction(data),
    onSuccess: (data) => {
      toast.success(`${data.action}d ${data.affected} prompts`);
      queryClient.invalidateQueries({ queryKey: ['trends-prompts'] });
      setSelectedIds(new Set());
    },
  });

  const convertToListingMutation = useMutation({
    mutationFn: (id: string) => api.trends.convertToListing(id),
    onSuccess: (data) => {
      toast.success(`Listing created: ${data.title.slice(0, 40)}...`);
      queryClient.invalidateQueries({ queryKey: ['trends-prompts'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      toast.error(`Failed to create listing: ${error}`);
    },
  });

  const niches = nichesData?.niches ?? [];
  const prompts = promptsData?.prompts ?? [];

  const handleGenerate = () => {
    const niche = selectedNiche === 'custom' ? customNiche : selectedNiche;
    if (!originalPrompt.trim()) {
      toast.error('Please enter an original prompt');
      return;
    }
    if (!niche.trim()) {
      toast.error('Please select or enter a niche');
      return;
    }
    generateMutation.mutate({
      original_prompt: originalPrompt.trim(),
      niche: niche.trim(),
      count: promptCount,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === prompts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(prompts.map((p) => p.id)));
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'generating':
        return 'bg-yellow-100 text-yellow-700';
      case 'generated':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-etsy-orange" />
            Trends Hijack
          </h1>
          <p className="text-gray-500 mt-1">
            Generate prompt variations from successful trend examples
          </p>
        </div>
      </div>

      {/* Generation Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-etsy-orange" />
          Generate Variations
        </h2>

        <div className="space-y-4">
          {/* Original Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Original Successful Prompt
            </label>
            <textarea
              value={originalPrompt}
              onChange={(e) => setOriginalPrompt(e.target.value)}
              placeholder="Paste a successful trend prompt here (e.g., from Printify trends)..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-etsy-orange focus:border-transparent resize-none"
              rows={6}
            />
          </div>

          {/* Niche and Count Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Niche Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Niche</label>
              <select
                value={selectedNiche}
                onChange={(e) => setSelectedNiche(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-etsy-orange focus:border-transparent"
              >
                <option value="">Select a niche...</option>
                {niches.map((niche) => (
                  <option key={niche} value={niche}>
                    {niche.charAt(0).toUpperCase() + niche.slice(1)}
                  </option>
                ))}
                <option value="custom">Custom niche...</option>
              </select>
            </div>

            {/* Custom Niche Input */}
            {selectedNiche === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Niche</label>
                <input
                  type="text"
                  value={customNiche}
                  onChange={(e) => setCustomNiche(e.target.value)}
                  placeholder="Enter custom niche..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-etsy-orange focus:border-transparent"
                />
              </div>
            )}

            {/* Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Variations
              </label>
              <select
                value={promptCount}
                onChange={(e) => setPromptCount(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-etsy-orange focus:border-transparent"
              >
                {[1, 3, 5, 10, 15, 20].map((n) => (
                  <option key={n} value={n}>
                    {n} variation{n > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={generateMutation.isPending || !originalPrompt.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-etsy-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate Variations
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-etsy-orange focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="generated">Generated</option>
          </select>
        </div>

        {/* Niche Filter */}
        <select
          value={nicheFilter}
          onChange={(e) => setNicheFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-etsy-orange focus:border-transparent"
        >
          <option value="">All Niches</option>
          {niches.map((niche) => (
            <option key={niche} value={niche}>
              {niche.charAt(0).toUpperCase() + niche.slice(1)}
            </option>
          ))}
        </select>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">{selectedIds.size} selected</span>
            <button
              onClick={() =>
                bulkActionMutation.mutate({ ids: Array.from(selectedIds), action: 'approve' })
              }
              className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
            >
              Approve
            </button>
            <button
              onClick={() =>
                bulkActionMutation.mutate({ ids: Array.from(selectedIds), action: 'reject' })
              }
              className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              Reject
            </button>
            <button
              onClick={() =>
                bulkActionMutation.mutate({ ids: Array.from(selectedIds), action: 'delete' })
              }
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Prompts List */}
      <div className="bg-white rounded-xl border border-gray-200">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-4">
          <button
            onClick={selectAll}
            className="p-1 hover:bg-gray-100 rounded"
            title={selectedIds.size === prompts.length ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === prompts.length && prompts.length > 0 ? (
              <CheckSquare className="w-5 h-5 text-etsy-orange" />
            ) : (
              <Square className="w-5 h-5 text-gray-400" />
            )}
          </button>
          <span className="text-sm font-medium text-gray-700">
            {prompts.length} prompt{prompts.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* List */}
        {loadingPrompts ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-etsy-orange mx-auto" />
            <p className="mt-2 text-gray-500">Loading prompts...</p>
          </div>
        ) : prompts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No prompts yet. Generate some variations above!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {prompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                isSelected={selectedIds.has(prompt.id)}
                onToggleSelect={() => toggleSelect(prompt.id)}
                onApprove={() => updateStatusMutation.mutate({ id: prompt.id, status: 'approved' })}
                onReject={() => updateStatusMutation.mutate({ id: prompt.id, status: 'rejected' })}
                onGenerateImage={() => generateImageMutation.mutate(prompt.id)}
                onConvertToListing={() => convertToListingMutation.mutate(prompt.id)}
                onDelete={() => deleteMutation.mutate(prompt.id)}
                onCopy={() => copyToClipboard(prompt.generated_prompt)}
                getStatusBadge={getStatusBadge}
                isGeneratingImage={
                  generateImageMutation.isPending && generateImageMutation.variables === prompt.id
                }
                isConvertingToListing={
                  convertToListingMutation.isPending &&
                  convertToListingMutation.variables === prompt.id
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Prompt Card Component
function PromptCard({
  prompt,
  isSelected,
  onToggleSelect,
  onApprove,
  onReject,
  onGenerateImage,
  onConvertToListing,
  onDelete,
  onCopy,
  getStatusBadge,
  isGeneratingImage,
  isConvertingToListing,
}: {
  prompt: TrendPrompt;
  isSelected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  onGenerateImage: () => void;
  onConvertToListing: () => void;
  onDelete: () => void;
  onCopy: () => void;
  getStatusBadge: (status: string) => string;
  isGeneratingImage: boolean;
  isConvertingToListing: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={clsx('p-4', isSelected && 'bg-orange-50')}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button onClick={onToggleSelect} className="mt-1 p-0.5 hover:bg-gray-100 rounded">
          {isSelected ? (
            <CheckSquare className="w-5 h-5 text-etsy-orange" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <span
              className={clsx(
                'px-2 py-0.5 rounded text-xs font-medium',
                getStatusBadge(prompt.status)
              )}
            >
              {prompt.status}
            </span>
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
              {prompt.niche}
            </span>
            <span className="text-xs text-gray-400">${prompt.total_cost_usd.toFixed(4)}</span>
          </div>

          {/* Prompt Text */}
          <div
            className={clsx('text-sm text-gray-700 cursor-pointer', !expanded && 'line-clamp-3')}
            onClick={() => setExpanded(!expanded)}
          >
            {prompt.generated_prompt}
          </div>

          {/* Image Preview */}
          {prompt.image_url && (
            <div className="mt-3">
              <img
                src={prompt.image_url}
                alt="Generated design"
                className="w-32 h-32 object-cover rounded-lg border border-gray-200"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onCopy}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
            title="Copy prompt"
          >
            <Copy className="w-4 h-4" />
          </button>

          {prompt.status === 'pending' && (
            <>
              <button
                onClick={onApprove}
                className="p-2 hover:bg-green-100 rounded-lg text-green-600 hover:text-green-700"
                title="Approve"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={onReject}
                className="p-2 hover:bg-red-100 rounded-lg text-red-600 hover:text-red-700"
                title="Reject"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}

          {(prompt.status === 'approved' || prompt.status === 'pending') && !prompt.image_url && (
            <button
              onClick={onGenerateImage}
              disabled={isGeneratingImage}
              className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 hover:text-blue-700 disabled:opacity-50"
              title="Generate image"
            >
              {isGeneratingImage ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Image className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Convert to Listing - only show if has image but no listing yet */}
          {prompt.image_url && !prompt.listing_id && (
            <button
              onClick={onConvertToListing}
              disabled={isConvertingToListing}
              className="p-2 hover:bg-green-100 rounded-lg text-green-600 hover:text-green-700 disabled:opacity-50"
              title="Convert to listing"
            >
              {isConvertingToListing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Package className="w-4 h-4" />
              )}
            </button>
          )}

          {/* View Listing - if already converted */}
          {prompt.listing_id && (
            <Link
              to={`/listing/${prompt.listing_id}`}
              className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 hover:text-blue-700"
              title="View listing"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          )}

          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-100 rounded-lg text-gray-400 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
